/**
 * CellRenderers.tsx - Built-in cell renderers for DataTable
 * 
 * CHANGE HISTORY:
 * - 251031_2000 [pending] danielbeer: Initial creation with badge, progress, tags, count renderers
 */

import React from 'react';

export interface BadgeConfig {
  colorMap?: Record<string, string>;
  defaultColor?: string;
}

export interface ProgressConfig {
  colorFn?: (value: number) => string;
  showPercentage?: boolean;
  height?: number;
}

export interface TagsConfig {
  maxTags?: number;
  tagStyle?: React.CSSProperties;
  moreStyle?: React.CSSProperties;
}

export interface CountConfig {
  icon?: string;
  emptyText?: string;
  emptyStyle?: React.CSSProperties;
}

/**
 * Badge Renderer - For status, category, etc.
 */
export const BadgeRenderer: React.FC<{
  value: string;
  config?: BadgeConfig;
}> = ({ value, config = {} }) => {
  const { colorMap = {}, defaultColor = '#6b7280' } = config;
  const color = colorMap[value] || defaultColor;

  return (
    <span
      style={{
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: 500,
        backgroundColor: `${color}20`,
        color: color,
        whiteSpace: 'nowrap',
      }}
    >
      {value}
    </span>
  );
};

/**
 * Progress Bar Renderer - For completion, progress, etc.
 */
export const ProgressRenderer: React.FC<{
  value: number;
  config?: ProgressConfig;
}> = ({ value, config = {} }) => {
  const {
    colorFn = (val) => (val >= 80 ? '#10b981' : val >= 50 ? '#f59e0b' : '#ef4444'),
    showPercentage = true,
    height = 8,
  } = config;

  const color = colorFn(value);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <div
        style={{
          flex: 1,
          height: `${height}px`,
          backgroundColor: '#e5e7eb',
          borderRadius: '4px',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${value}%`,
            height: '100%',
            backgroundColor: color,
            transition: 'width 0.3s ease',
          }}
        />
      </div>
      {showPercentage && (
        <span style={{ fontSize: '12px', fontWeight: 500, minWidth: '40px' }}>
          {value}%
        </span>
      )}
    </div>
  );
};

/**
 * Tags Renderer - For arrays of tags
 */
export const TagsRenderer: React.FC<{
  value: string[] | undefined;
  config?: TagsConfig;
}> = ({ value, config = {} }) => {
  const {
    maxTags = 3,
    tagStyle = {
      padding: '2px 8px',
      backgroundColor: 'rgba(59, 130, 246, 0.1)',
      color: '#3b82f6',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: 500,
    },
    moreStyle = {
      fontSize: '12px',
      color: '#9ca3af',
      fontWeight: 500,
    },
  } = config;

  if (!value || value.length === 0) {
    return <span style={{ color: '#9ca3af' }}>—</span>;
  }

  return (
    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
      {value.slice(0, maxTags).map((tag, idx) => (
        <span key={idx} style={tagStyle}>
          {tag}
        </span>
      ))}
      {value.length > maxTags && (
        <span style={moreStyle}>+{value.length - maxTags}</span>
      )}
    </div>
  );
};

/**
 * Count Renderer - For dependencies, dependents, etc.
 */
export const CountRenderer: React.FC<{
  value: string[] | number | undefined;
  config?: CountConfig;
}> = ({ value, config = {} }) => {
  const {
    icon = '',
    emptyText = '—',
    emptyStyle = { color: '#9ca3af' },
  } = config;

  const count = Array.isArray(value) ? value.length : typeof value === 'number' ? value : 0;

  if (count === 0) {
    return <span style={emptyStyle}>{emptyText}</span>;
  }

  return (
    <span>
      {icon && `${icon} `}
      {count}
    </span>
  );
};

/**
 * Multi-line Text Renderer - For name + ID, etc.
 */
export const MultiLineRenderer: React.FC<{
  primary: string;
  secondary?: string;
  primaryStyle?: React.CSSProperties;
  secondaryStyle?: React.CSSProperties;
}> = ({ primary, secondary, primaryStyle = {}, secondaryStyle = {} }) => {
  return (
    <div>
      <div style={{ fontWeight: 600, ...primaryStyle }}>{primary}</div>
      {secondary && (
        <div style={{ fontSize: '12px', color: '#6b7280', ...secondaryStyle }}>
          {secondary}
        </div>
      )}
    </div>
  );
};

/**
 * Get renderer by type
 */
export const getCellRenderer = (
  type: 'badge' | 'progress' | 'tags' | 'count' | 'multiline' | 'text',
  value: any,
  config?: any
) => {
  switch (type) {
    case 'badge':
      return <BadgeRenderer value={value} config={config} />;
    case 'progress':
      return <ProgressRenderer value={value} config={config} />;
    case 'tags':
      return <TagsRenderer value={value} config={config} />;
    case 'count':
      return <CountRenderer value={value} config={config} />;
    case 'multiline':
      return <MultiLineRenderer {...config} primary={value} />;
    case 'text':
    default:
      return value;
  }
};
